import { Skeleton, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import Highcharts from 'highcharts';
import HighChartsReact from 'highcharts-react-official';
import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import {
  themeModeAtom,
  themeModeEnum,
} from '../../../../recoil/atoms/themeModeAtom';
import { MetricChartProps } from './MetricTypes';

const useStyles = makeStyles((theme: Theme) => ({
  metricPanelContainerOutline: {
    width: '31%',
    maxWidth: '300px',
    minWidth: '220px',
  },
  metricPanelContainerOutlineFullWidth: {
    width: '100%',
  },
  metricPanelContainerBox: {
    padding: '0px',
    margin: '8px',
    display: 'flex',
    justifyContent: 'center',
  },
  metricPanelTopRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  metricPanelTitle: {
    fontSize: '14px',
    fontWeight: 500,
    textAlign: 'start',
  },
  mainText: {
    color: theme.palette.mode === 'dark' ? '#fff' : 'black',
  },
  metricPanelBottomRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  metricPanelBottomRowLeftColumnContainer: {},
  metricPanelBottomRowRightColumnContainer: {},
  metricPanelData: {
    fontWeight: 600,
    fontSize: '28px',
    marginBottom: '0px',
  },
  metricPanelUpwardArrow: {
    height: '36px',
    width: '36px',
    color: 'rgb(63,200,183)',
  },
  metricPanelDownwardArrow: {
    height: '36px',
    width: '36px',
    color: 'rgb(251,91,93)',
  },
  metricPanelHorizontalArrow: {
    height: '40px',
    width: '40px',
  },
  metricPanelDeltaContainerOutline: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  metricPanelArrowContainerOutline: {},
  metricPanelArrowIconContainerOutline: {
    width: '40px',
    display: 'flex',
    margin: '0px 8px',
    justifyContent: 'center',
  },
  metricPanelPercentageChangeContainerOutline: {},
  metricPanelPercentageChangeSubContainerOutline: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  metricPanelPercentageChange: {
    fontWeight: 600,
    fontSize: '18px',
    marginBottom: '0px',
  },
  metricPanelText: {
    fontSize: '10px',
    fontWeight: 300,
    marginBottom: '0px',
  },
  questionMarkIcon: {
    borderRadius: '50%',
    height: '16px',
    width: '16px',
    padding: '2px',
  },
  metricPanelSkeletonContainerOutline: {
    width: '22%',
    maxWidth: '268px',
    minWidth: '220px',
    height: '96px',
  },
  metricPanelSkeletonContainerBox: {
    padding: '0px',
    margin: '16px',
  },
  skeleton: {
    backgroundColor: theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey',
    height: '6em',
    margin: 10,
  },
}));

const MetricChart = (props: {
  data: {
    [key: string]: MetricChartProps[];
  };
  title: string;
  loading: boolean;
  height?: number;
  width?: number;
  fullWidth?: boolean;
  align?: string;
  verticalAlign?: string;
  colorArr?: string[];
}) => {
  const classes = useStyles();
  const chartRef = useRef(null);
  const themeMode = useRecoilValue(themeModeAtom);

  useEffect(() => {
    Highcharts.setOptions({
      lang: {
        thousandsSep: '',
      },
    });
  }, []);

  const pad = (num) => {
    return ('0' + num).slice(-2);
  };
  const mmss = (secs) => {
    const minutes = Math.floor(secs / 60);
    secs = Math.floor(secs % 60);
    return `${minutes}:${pad(secs)}`;
  };
  const total = props.data?.[props.title]?.reduce(
    (a: number, b: MetricChartProps) => {
      return a + ((b?.data as number) ?? 0);
    },
    0
  );
  const titleUnit =
    props.data?.[props.title]?.[0]?.titleUnit ??
    props.data?.[props.title]?.[0]?.unit ??
    '';
  const options = {
    xAxis: {
      title: {
        enabled: true,
        // we want to display it in minutes but it is given to us in seconds
        text: `${
          titleUnit === 'Minutes' ? mmss(total) : total
        } Total ${titleUnit}`,
      },
    },
    credits: {
      enabled: false,
    },
    chart: {
      backgroundColor: 'rgba(0,0,0,0)',
      plotBackgroundColor: 'rgba(0,0,0,0)',
      plotBorderWidth: undefined,
      plotShadow: false,
      type: 'pie',
      width: props.width ?? 230,
      height: props.height ?? 370,
    },
    colors: props.colorArr?.length
      ? props.colorArr
      : ['#91D2E8', '#0098C7', 'green', 'orange'],
    title: {
      text: props.title,
      style: {
        fontWeight: 'normal',
        fontSize: '16px',
        color: themeMode === themeModeEnum.DARK_MODE ? '#fff' : '#333333',
      },
    },
    tooltip: {
      pointFormat:
        '<span style="color:{point.color} ">\u25CF</span> ' +
        '<b> {point.y} {point.unit}</b>',
    },
    legend: {
      labelFormat: `${titleUnit === 'Minutes' ? '{title} hh:mm' : '{title}'}`,
      align: props.align ?? 'center',
      verticalAlign: props.verticalAlign ?? 'bottom',
      title: {
        text: `${
          titleUnit === 'Minutes' ? mmss(total) : total
        } Total ${titleUnit}`,
        style: {
          fontStyle: 'normal',
          color: themeMode === themeModeEnum.DARK_MODE ? '#fff' : 'black',
        },
      },
      layout: 'vertical',
      itemMarginTop: 8,
      itemMarginBottom: 8,
      itemStyle: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        color: themeMode === themeModeEnum.DARK_MODE ? '#fff' : '#333333',
      },
      symbolHeight: 16,
      symbolWidth: 16,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
        size: '100%',
        innerSize: '75%',
      },
    },
    series: [
      {
        name: props.title,
        colorByPoint: true,
        events: {
          click: function (e) {
            e.preventDefault(); // prevent any action from occuring on "click" event
          },
        },
        data: props.data?.[props.title]?.map(
          (elem: MetricChartProps, index: number) => {
            return {
              name: elem.title,
              title: `${
                titleUnit === 'Minutes'
                  ? `${elem.title} - ${elem.displayData}`
                  : `${elem.title} - ${elem.displayData} ${elem.unit}`
              }`,
              unit: elem.unit,
              y: Number(elem.data),
            };
          }
        ),
      },
    ],
  };

  if (props.loading) {
    return (
      <div
        className={clsx(
          classes.metricPanelContainerBox,
          props.fullWidth
            ? classes.metricPanelContainerOutlineFullWidth
            : classes.metricPanelContainerOutline
        )}
      >
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        classes.metricPanelContainerBox,
        props.fullWidth
          ? classes.metricPanelContainerOutlineFullWidth
          : classes.metricPanelContainerOutline
      )}
    >
      <HighChartsReact
        allowChartUpdate={true}
        highcharts={Highcharts}
        options={options}
        loading={props.loading}
        ref={chartRef}
      />
    </div>
  );
};
export default MetricChart;
